import React, { useState, useRef } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { FiPaperclip, FiX } from 'react-icons/fi';
import toast from 'react-hot-toast';
import CustomerPicker from '../../components/shared/CustomerPicker';
import CurrencySelect from '../../components/shared/CurrencySelect';
import CountrySelect from '../../components/shared/CountrySelect';
import FileUploader from '../../components/ui/FileUploader';
import ErrorBanner from '../../components/ui/ErrorBanner';
import { createInvoice } from '../../api/invoices';
import { uploadDocument } from '../../api/documents';
import { toIsoDate } from '../../utils/date';
import { getCountries, getCurrencies } from '../../api/reference';

// ── Shared styles (same as InvoiceFormInner) ────────────────────────────────
const labelClass =
  'block text-[11px] font-medium text-slate-500 uppercase tracking-[0.04em] mb-[5px]';
const inputClass =
  'w-full h-[34px] border border-slate-300 rounded-md bg-white text-slate-900 text-[13px] px-[10px] focus:outline-none focus:border-blue-500';
const optionalTag = (
  <span className="text-[11px] font-normal text-slate-400 normal-case tracking-normal ml-1">
    (optional)
  </span>
);

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className={labelClass}>
        {label}
        {required ? <span className="text-[#D85A30] ml-0.5">*</span> : optionalTag}
      </label>
      {children}
      {error && <p className="mt-1 text-[11px] text-red-500">{error}</p>}
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export default function ExternalInvoiceForm({ onCreated }) {
  const qc = useQueryClient();

  const [customerId, setCustomerId]         = useState(null);
  const [invoiceDate, setInvoiceDate]       = useState(toIsoDate(new Date()));
  const [dueDate, setDueDate]               = useState('');
  const [currency, setCurrency]             = useState('INR');
  const [billingCountry, setBillingCountry] = useState('IN');
  const [notes, setNotes]                   = useState('');
  const [poNumber, setPoNumber]             = useState('');
  const [contactPerson, setContactPerson]   = useState('');
  const [file, setFile]                     = useState(null);
  const [errors, setErrors]                 = useState({});
  const [submitError, setSubmitError]       = useState(null);

  // Mirrors the same currency-override pattern used in InvoiceFormInner
  const userOverrideCurrency = useRef(false);

  // ── Reference data ─────────────────────────────────────────────────────────

  const { data: countries = [] } = useQuery({
    queryKey: ['reference', 'countries'],
    queryFn: getCountries,
    staleTime: 24 * 60 * 60 * 1000,
  });

  const { data: currencies = [] } = useQuery({
    queryKey: ['reference', 'currencies'],
    queryFn: getCurrencies,
    staleTime: 24 * 60 * 60 * 1000,
  });

  const orderedCurrencies = React.useMemo(() => {
    const order = { USD: 1, GBP: 2, EUR: 3, INR: 4 };
    return [...currencies].sort((a, b) => {
      const oA = order[a.code] || 99;
      const oB = order[b.code] || 99;
      return oA !== oB ? oA - oB : a.code.localeCompare(b.code);
    });
  }, [currencies]);

  const countryCurrencyMap = React.useMemo(() => {
    const map = {};
    countries.forEach((c) => { if (c.currencyCode) map[c.code] = c.currencyCode; });
    return {
      IN: 'INR', US: 'USD', GB: 'GBP', AU: 'AUD', CA: 'CAD',
      SG: 'SGD', AE: 'AED', JP: 'JPY', CN: 'CNY', CH: 'CHF',
      DE: 'EUR', FR: 'EUR', NL: 'EUR',
      ...map,
    };
  }, [countries]);

  React.useEffect(() => {
    if (userOverrideCurrency.current) return;
    const mapped = countryCurrencyMap[billingCountry];
    if (mapped) setCurrency(mapped);
  }, [billingCountry, countryCurrencyMap]);

  // ── Mutations ──────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (payload) => createInvoice(payload),
    onSuccess: async (data) => {
      // Always invalidate and notify — invoice record is created
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['revenue'] });

      // Best-effort file upload: try to attach the file to the invoice
      if (file && data?.id) {
        try {
          await uploadDocument({ file, entityType: 'INVOICE', entityId: data.id });
          toast.success(`External invoice ${data?.invoiceNumber || ''} uploaded with attachment`);
        } catch {
          // Invoice saved — only the attachment failed
          toast.success(`Invoice ${data?.invoiceNumber || ''} created`);
          toast.error('File could not be attached — ask your admin to enable invoice attachments on the server.', { duration: 6000 });
        }
      } else {
        toast.success(`External invoice ${data?.invoiceNumber || ''} created`);
      }
      onCreated?.(data);
    },
    onError: (err) => {
      setSubmitError(err);
      toast.error('Upload failed — see error above');
    },
  });

  // ── Validation ─────────────────────────────────────────────────────────────

  const validate = () => {
    const errs = {};
    if (!customerId) errs.customerId = 'Customer is required';
    if (!file) errs.file = 'Please attach the invoice file';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Submit (called by parent modal footer via form id) ─────────────────────

  const handleSubmit = (e) => {
    e.preventDefault();
    if (createMutation.isPending) return; // prevent double-submit
    setSubmitError(null);
    if (!validate()) return;

    createMutation.mutate({
      customerId: Number(customerId),
      source: 'EXTERNAL',
      currency: currency || 'INR',
      billingCountry: billingCountry || 'IN',
      ...(invoiceDate   ? { invoiceDate }   : {}),
      ...(dueDate       ? { dueDate }       : {}),
      ...(notes         ? { notes }         : {}),
      ...(poNumber      ? { poNumber }      : {}),
      ...(contactPerson ? { contactPerson } : {}),
      items: [],
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <form id="external-invoice-form" onSubmit={handleSubmit} className="flex flex-col gap-[14px]">
      {submitError && (
        <ErrorBanner error={submitError} title="Could not upload invoice" />
      )}

      {/* Customer — required */}
      <Field label="Customer" required error={errors.customerId}>
        <CustomerPicker
          value={customerId}
          onChange={(id) => {
            setCustomerId(id);
            setErrors((e) => ({ ...e, customerId: null }));
          }}
          onCustomerSelect={(c) => {
            if (!c) return;
            setCustomerId(c.id);
            if (c.billingCountry) {
              userOverrideCurrency.current = false;
              setBillingCountry(c.billingCountry);
            }
            if (c.currency) {
              userOverrideCurrency.current = true;
              setCurrency(c.currency);
            }
          }}
          error={!!errors.customerId}
        />
      </Field>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-[10px]">
        <Field label="Invoice Date">
          <input
            type="date"
            value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Due Date">
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className={inputClass}
          />
        </Field>
      </div>

      {/* Country & Currency */}
      <div className="grid grid-cols-2 gap-[10px]">
        <Field label="Billing Country">
          <CountrySelect
            countries={countries}
            value={billingCountry}
            onChange={(code) => {
              userOverrideCurrency.current = false;
              setBillingCountry(code);
            }}
          />
        </Field>
        <Field label="Currency">
          <CurrencySelect
            currencies={orderedCurrencies}
            value={currency}
            onChange={(code) => {
              userOverrideCurrency.current = true;
              setCurrency(code);
            }}
          />
        </Field>
      </div>

      {/* PO Number & Contact Person */}
      <div className="grid grid-cols-2 gap-[10px]">
        <Field label="PO Number">
          <input
            type="text"
            value={poNumber}
            onChange={(e) => setPoNumber(e.target.value)}
            placeholder="e.g. PO-20240001"
            className={inputClass}
          />
        </Field>
        <Field label="Contact Person">
          <input
            type="text"
            value={contactPerson}
            onChange={(e) => setContactPerson(e.target.value)}
            placeholder="e.g. John Smith"
            className={inputClass}
          />
        </Field>
      </div>

      {/* Notes */}
      <Field label="Notes">
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Any additional context..."
          className="w-full border border-slate-300 rounded-md bg-white text-slate-900 text-[13px] px-[10px] py-[6px] focus:outline-none focus:border-blue-500 resize-none"
        />
      </Field>

      {/* File — required */}
      <Field label="Invoice File" required error={errors.file}>
        {file ? (
          <div className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2">
            <div className="flex min-w-0 items-center gap-2 text-sm">
              <FiPaperclip className="shrink-0 text-slate-400" size={14} />
              <span className="truncate text-slate-700">{file.name}</span>
              <span className="shrink-0 text-xs text-slate-400">
                {(file.size / 1024).toFixed(1)} KB
              </span>
            </div>
            <button
              type="button"
              onClick={() => { setFile(null); setErrors((e) => ({ ...e, file: null })); }}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-500 transition-colors"
              aria-label="Remove file"
            >
              <FiX size={15} />
            </button>
          </div>
        ) : (
          <FileUploader
            variant="dropzone"
            accept=".pdf,.jpg,.jpeg,.png"
            onSelect={(f) => { setFile(f); setErrors((e) => ({ ...e, file: null })); }}
          />
        )}
      </Field>
    </form>
  );
}

/** Expose the form id so the parent modal footer can `form="..."` the submit button. */
ExternalInvoiceForm.formId = 'external-invoice-form';

/** Returns true while either mutation is in-flight — used by parent to show loading. */
ExternalInvoiceForm.useIsSubmitting = () => false; // parent reads createMutation.isPending directly
ExternalInvoiceForm.formId = 'external-invoice-form';
